import React, { useState, useEffect } from 'react';
import {
  LocalMatchday,
  LocalMatch,
  CreateLocalMatchdayRequest,
  UpdateLocalMatchdayRequest,
  CreateLocalMatchRequest,
  UpdateLocalMatchRequest,
  getLocalMatchdayStatusInfo,
  getLocalMatchStatusInfo,
  formatDate,
  formatTime,
  formatDateTime,
  formatLocalMatchResult,
  formatLocalMatchTeams
} from '../types/localMatchday';
import { LocalMatchdayApi, LocalMatchApi } from '../services/localMatchdayApi';
import LocalMatchdayForm from '../components/localMatchday/LocalMatchdayForm';
import LocalMatchForm from '../components/localMatch/LocalMatchForm';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { ToastContainer } from '../components/common/Toast';
import { useToast } from '../hooks/useToast';
import { Plus, Pencil, Trash2, Pause, Play, LayoutGrid, List, Building2, CalendarDays, Clock, MapPin, User, Users, CheckCircle, CircleDot } from 'lucide-react';
import { usePreferredViewMode, STANDARD_VIEW_MODE_MAP } from '../hooks/usePreferredViewMode';
import '../styles/PageLayout.css';

const LocalMatchdays: React.FC = () => {
  const [localMatchdays, setLocalMatchdays] = useState<LocalMatchday[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = usePreferredViewMode<'cards' | 'list'>('cards', STANDARD_VIEW_MODE_MAP);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingLocalMatchday, setEditingLocalMatchday] = useState<LocalMatchday | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [localMatchdayToDelete, setLocalMatchdayToDelete] = useState<LocalMatchday | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Match management state
  const [selectedMatchdayId, setSelectedMatchdayId] = useState<number | null>(null);
  const [matchdayMatches, setMatchdayMatches] = useState<LocalMatch[]>([]);
  const [showMatchForm, setShowMatchForm] = useState(false);
  const [editingMatch, setEditingMatch] = useState<LocalMatch | null>(null);
  const [matchFormLoading, setMatchFormLoading] = useState(false);
  const [showMatchDeleteDialog, setShowMatchDeleteDialog] = useState(false);
  const [matchToDelete, setMatchToDelete] = useState<LocalMatch | null>(null);
  const [matchDeleteLoading, setMatchDeleteLoading] = useState(false);

  const { toasts, removeToast, showSuccess, showError } = useToast();

  useEffect(() => {
    loadLocalMatchdays();
  }, [showInactive]);

  const loadLocalMatchdays = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await LocalMatchdayApi.getAllLocalMatchdays(showInactive);
      setLocalMatchdays(data);
    } catch (err) {
      setError('Fehler beim Laden der lokalen Spieltage');
      console.error('Error loading local matchdays:', err);
      showError('Fehler beim Laden der lokalen Spieltage');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLocalMatchday = async (data: CreateLocalMatchdayRequest) => {
    try {
      setFormLoading(true);
      await LocalMatchdayApi.createLocalMatchday(data);
      await loadLocalMatchdays();
      setShowForm(false);
      showSuccess('Lokaler Spieltag erfolgreich erstellt');
    } catch (err) {
      console.error('Error creating local matchday:', err);
      showError('Fehler beim Erstellen des lokalen Spieltags');
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateLocalMatchday = async (data: UpdateLocalMatchdayRequest) => {
    if (!editingLocalMatchday) return;

    try {
      setFormLoading(true);
      await LocalMatchdayApi.updateLocalMatchday(editingLocalMatchday.id, data);
      await loadLocalMatchdays();
      setEditingLocalMatchday(null);
      setShowForm(false);
      showSuccess('Lokaler Spieltag erfolgreich aktualisiert');
    } catch (err) {
      console.error('Error updating local matchday:', err);
      showError('Fehler beim Aktualisieren des lokalen Spieltags');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteLocalMatchday = (localMatchday: LocalMatchday) => {
    setLocalMatchdayToDelete(localMatchday);
    setShowDeleteDialog(true);
  };

  const confirmDeleteLocalMatchday = async () => {
    if (!localMatchdayToDelete) return;

    try {
      setDeleteLoading(true);
      const canDelete = await LocalMatchdayApi.canDeleteLocalMatchday(localMatchdayToDelete.id);
      if (!canDelete) {
        showError('Lokaler Spieltag kann nicht gelöscht werden (hat zugehörige Spiele)');
        setShowDeleteDialog(false);
        setLocalMatchdayToDelete(null);
        return;
      }

      await LocalMatchdayApi.deleteLocalMatchday(localMatchdayToDelete.id);
      await loadLocalMatchdays();

      showSuccess('Lokaler Spieltag erfolgreich gelöscht');
    } catch (err) {
      console.error('Error deleting local matchday:', err);
      showError('Fehler beim Löschen des lokalen Spieltags');
    } finally {
      setDeleteLoading(false);
      setShowDeleteDialog(false);
      setLocalMatchdayToDelete(null);
    }
  };

  const handleToggleActive = async (localMatchday: LocalMatchday) => {
    try {
      await LocalMatchdayApi.toggleLocalMatchdayActive(localMatchday.id);
      await loadLocalMatchdays();
      showSuccess(
        `Lokaler Spieltag erfolgreich ${localMatchday.isActive ? 'deaktiviert' : 'aktiviert'}`
      );
    } catch (err) {
      console.error('Error toggling local matchday status:', err);
      showError('Fehler beim Ändern des Status');
    }
  };

  const handleEditLocalMatchday = (localMatchday: LocalMatchday) => {
    setEditingLocalMatchday(localMatchday);
    setShowForm(true);
  };

  // Match management functions
  const loadMatchdayMatches = async (matchdayId: number) => {
    try {
      // Load all matches including inactive ones for the modal view
      const matches = await LocalMatchApi.getLocalMatchesByMatchday(matchdayId);
      setMatchdayMatches(matches);
    } catch (error) {
      console.error('Error loading matchday matches:', error);
      showError('Fehler beim Laden der Spiele');
    }
  };

  const handleViewMatches = async (localMatchday: LocalMatchday) => {
    setSelectedMatchdayId(localMatchday.id);
    await loadMatchdayMatches(localMatchday.id);
  };

  const handleAddMatch = (localMatchday: LocalMatchday) => {
    setSelectedMatchdayId(localMatchday.id);
    setEditingMatch(null);
    setShowMatchForm(true);
  };

  const handleEditMatch = (match: LocalMatch) => {
    setEditingMatch(match);
    setShowMatchForm(true);
  };

  const handleCreateMatch = async (request: CreateLocalMatchRequest) => {
    if (!selectedMatchdayId) return;

    try {
      setMatchFormLoading(true);
      const matchRequest = { ...request, localMatchdayId: selectedMatchdayId };
      await LocalMatchApi.createLocalMatch(matchRequest);
      await loadMatchdayMatches(selectedMatchdayId);
      await loadLocalMatchdays(); // Refresh matchdays to update match counts
      setShowMatchForm(false);
      showSuccess('Spiel erfolgreich erstellt');
    } catch (error) {
      console.error('Error creating match:', error);
      showError('Fehler beim Erstellen des Spiels');
    } finally {
      setMatchFormLoading(false);
    }
  };

  const handleUpdateMatch = async (id: number, request: UpdateLocalMatchRequest) => {
    try {
      setMatchFormLoading(true);
      await LocalMatchApi.updateLocalMatch(id, request);
      if (selectedMatchdayId) {
        await loadMatchdayMatches(selectedMatchdayId);
      }
      await loadLocalMatchdays(); // Refresh matchdays
      setShowMatchForm(false);
      setEditingMatch(null);
      showSuccess('Spiel erfolgreich aktualisiert');
    } catch (error) {
      console.error('Error updating match:', error);
      showError('Fehler beim Aktualisieren des Spiels');
    } finally {
      setMatchFormLoading(false);
    }
  };

  const handleDeleteMatch = (match: LocalMatch) => {
    setMatchToDelete(match);
    setShowMatchDeleteDialog(true);
  };

  const confirmDeleteMatch = async () => {
    if (!matchToDelete || !selectedMatchdayId) return;

    try {
      setMatchDeleteLoading(true);
      const success = await LocalMatchApi.deleteLocalMatch(matchToDelete.id);
      if (success) {
        await loadMatchdayMatches(selectedMatchdayId);
        await loadLocalMatchdays(); // Refresh matchdays to update match counts
        showSuccess('Spiel erfolgreich gelöscht');
      }
    } catch (error) {
      console.error('Error deleting match:', error);
      showError('Fehler beim Löschen des Spiels');
    } finally {
      setMatchDeleteLoading(false);
      setShowMatchDeleteDialog(false);
      setMatchToDelete(null);
    }
  };

  const handleToggleMatchActive = async (match: LocalMatch) => {
    try {
      await LocalMatchApi.toggleLocalMatchActive(match.id);
      if (selectedMatchdayId) {
        await loadMatchdayMatches(selectedMatchdayId);
      }
      await loadLocalMatchdays(); // Refresh matchdays to update counts
      showSuccess(`Spiel erfolgreich ${match.isActive ? 'deaktiviert' : 'aktiviert'}`);
    } catch (error) {
      console.error('Error toggling match status:', error);
      showError('Fehler beim Ändern des Spiel-Status');
    }
  };

  const filteredLocalMatchdays = localMatchdays.filter(localMatchday => {
    const matchesSearch = searchTerm === '' || 
      localMatchday.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      localMatchday.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      localMatchday.organizer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      localMatchday.description?.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesSearch;
  });

  const stats = {
    total: filteredLocalMatchdays.length,
    active: filteredLocalMatchdays.filter(lm => lm.isActive).length,
    inactive: filteredLocalMatchdays.filter(lm => !lm.isActive).length,
    completed: filteredLocalMatchdays.filter(lm => lm.isCompleted).length,
    upcoming: filteredLocalMatchdays.filter(lm => new Date(lm.date) > new Date() && !lm.isCompleted).length
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-loading">Lade lokale Spieltage...</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Controls – two-row Menüband */}
      <div className="page-controls">
        {/* Row 1: Buttons */}
        <div className="page-controls-row">
          <button className="page-btn page-btn-create" onClick={() => setShowForm(true)} title="Neuer Spieltag">
            <Plus size={14} /> Neu
          </button>
          <label className="page-checkbox-label">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
            Inaktive anzeigen
          </label>
          <div className="page-controls-spacer" />
          <div className="page-view-toggle">
            <button className={`page-toggle-btn ${viewMode === 'cards' ? 'active' : ''}`} onClick={() => setViewMode('cards')} title="Kacheln">
              <LayoutGrid size={14} />
            </button>
            <button className={`page-toggle-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')} title="Liste">
              <List size={14} />
            </button>
          </div>
        </div>
        {/* Row 2: Search + record count */}
        <div className="page-controls-row">
          <input
            type="text"
            className="page-search-input"
            placeholder="Suchen nach Name, Ort, Organisator..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="page-controls-spacer" />
          <span className="page-record-count">{stats.total} Gefunden · {stats.active} Aktiv · {stats.completed} Abgeschlossen · {stats.upcoming} Bevorstehend</span>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="page-error">
          <p>{error}</p>
          <button onClick={loadLocalMatchdays}>Erneut versuchen</button>
        </div>
      )}

      {/* Content */}
      {filteredLocalMatchdays.length === 0 ? (
        <div className="page-empty-state">
          <Building2 size={32} />
          <h3>Keine lokalen Spieltage gefunden</h3>
          <p>Versuchen Sie andere Filter oder Suchbegriffe.</p>
        </div>
      ) : viewMode === 'cards' ? (
        <div className="cards-grid">
          {filteredLocalMatchdays.map((localMatchday) => {
            const statusInfo = getLocalMatchdayStatusInfo(localMatchday.status);
            return (
              <div key={localMatchday.id} className="page-card">
                <div className="page-card-header">
                  <h3><Building2 size={14} /> {localMatchday.name}</h3>
                  <div className="page-card-badges">
                    <span className="page-badge" style={{ backgroundColor: statusInfo.color }}>
                      {statusInfo.icon} {statusInfo.label}
                    </span>
                    {!localMatchday.isActive && (
                      <span className="page-badge" style={{ backgroundColor: '#dc3545' }}>Inaktiv</span>
                    )}
                  </div>
                </div>

                <div className="page-card-body">
                  <div className="page-card-info">
                    <div><CalendarDays size={12} /> <strong>Datum:</strong> {formatDate(localMatchday.date)}</div>
                    {localMatchday.startTime && (
                      <div><Clock size={12} /> <strong>Zeit:</strong> {formatTime(localMatchday.startTime)}{localMatchday.endTime && ` - ${formatTime(localMatchday.endTime)}`}</div>
                    )}
                    {localMatchday.location && (
                      <div><MapPin size={12} /> <strong>Ort:</strong> {localMatchday.location}</div>
                    )}
                    {localMatchday.organizer && (
                      <div><User size={12} /> <strong>Organisator:</strong> {localMatchday.organizer}</div>
                    )}
                    <div><CircleDot size={12} /> <strong>Spiele:</strong> {localMatchday.matchCount}</div>
                    {localMatchday.expectedAttendees && (
                      <div><Users size={12} /> <strong>Erwartete Teilnehmer:</strong> {localMatchday.expectedAttendees}</div>
                    )}
                    {localMatchday.actualAttendees && (
                      <div><CheckCircle size={12} /> <strong>Tatsächliche Teilnehmer:</strong> {localMatchday.actualAttendees}</div>
                    )}
                  </div>

                  {localMatchday.description && (
                    <div className="page-card-description"><strong>Beschreibung:</strong> {localMatchday.description}</div>
                  )}

                  {localMatchday.notes && (
                    <div className="page-card-notes"><strong>Notizen:</strong> {localMatchday.notes}</div>
                  )}
                </div>

                <div className="page-card-actions">
                  <button className="page-btn-icon" onClick={() => handleViewMatches(localMatchday)} title="Spiele anzeigen"><CircleDot size={14} /></button>
                  <button className="page-btn-icon" onClick={() => handleAddMatch(localMatchday)} title="Spiel hinzufügen"><Plus size={14} /></button>
                  <button className="page-btn-icon edit" onClick={() => handleEditLocalMatchday(localMatchday)} title="Bearbeiten"><Pencil size={14} /></button>
                  <button className="page-btn-icon" onClick={() => handleToggleActive(localMatchday)} title={localMatchday.isActive ? 'Deaktivieren' : 'Aktivieren'}>
                    {localMatchday.isActive ? <Pause size={14} /> : <Play size={14} />}
                  </button>
                  <button className="page-btn-icon delete" onClick={() => handleDeleteLocalMatchday(localMatchday)} title="Löschen"><Trash2 size={14} /></button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        // List view
        <div className="page-data-panel">
          <table className="page-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Datum</th>
                <th>Ort</th>
                <th>Organisator</th>
                <th>Spiele</th>
                <th>Teilnehmer</th>
                <th>Status</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {filteredLocalMatchdays.map((localMatchday) => {
                const statusInfo = getLocalMatchdayStatusInfo(localMatchday.status);
                return (
                  <tr key={localMatchday.id}>
                    <td><Building2 size={14} /> <strong>{localMatchday.name}</strong></td>
                    <td>
                      {formatDate(localMatchday.date)}
                      {localMatchday.startTime && (
                        <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>{formatTime(localMatchday.startTime)}</div>
                      )}
                    </td>
                    <td>{localMatchday.location || '-'}</td>
                    <td>{localMatchday.organizer || '-'}</td>
                    <td><strong>{localMatchday.matchCount}</strong></td>
                    <td>
                      {localMatchday.expectedAttendees ? (
                        <div>
                          <div>Erwartet: {localMatchday.expectedAttendees}</div>
                          {localMatchday.actualAttendees !== null && (
                            <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>Tatsächlich: {localMatchday.actualAttendees}</div>
                          )}
                        </div>
                      ) : '-'}
                    </td>
                    <td>
                      <span className="page-badge" style={{ backgroundColor: statusInfo.color }}>
                        {statusInfo.icon} {statusInfo.label}
                      </span>
                      {!localMatchday.isActive && (
                        <span className="page-badge" style={{ backgroundColor: '#dc3545' }}>Inaktiv</span>
                      )}
                    </td>
                    <td>
                      <div className="page-table-actions">
                        <button className="page-btn-icon" onClick={() => handleViewMatches(localMatchday)} title="Spiele anzeigen"><CircleDot size={14} /></button>
                        <button className="page-btn-icon" onClick={() => handleAddMatch(localMatchday)} title="Spiel hinzufügen"><Plus size={14} /></button>
                        <button className="page-btn-icon edit" onClick={() => handleEditLocalMatchday(localMatchday)} title="Bearbeiten"><Pencil size={14} /></button>
                        <button className="page-btn-icon" onClick={() => handleToggleActive(localMatchday)} title={localMatchday.isActive ? 'Deaktivieren' : 'Aktivieren'}>
                          {localMatchday.isActive ? <Pause size={14} /> : <Play size={14} />}
                        </button>
                        <button className="page-btn-icon delete" onClick={() => handleDeleteLocalMatchday(localMatchday)} title="Löschen"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <LocalMatchdayForm
          localMatchday={editingLocalMatchday || undefined}
          onSubmit={editingLocalMatchday ? handleUpdateLocalMatchday : handleCreateLocalMatchday}
          onCancel={() => {
            setShowForm(false);
            setEditingLocalMatchday(null);
          }}
          loading={formLoading}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        title="Lokalen Spieltag löschen"
        message={`Möchten Sie den lokalen Spieltag "${localMatchdayToDelete?.name}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`}
        confirmText="Löschen"
        cancelText="Abbrechen"
        onConfirm={confirmDeleteLocalMatchday}
        onCancel={() => {
          setShowDeleteDialog(false);
          setLocalMatchdayToDelete(null);
        }}
        loading={deleteLoading}
        type="danger"
      />

      {/* Matches View Modal */}
      {selectedMatchdayId && (
        <div className="page-modal-overlay">
          <div className="page-modal-content" style={{ maxWidth: '800px', width: '95%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2 style={{ margin: 0 }}>Spiele für Spieltag</h2>
              <button className="page-btn-icon" onClick={() => { setSelectedMatchdayId(null); setMatchdayMatches([]); }}>✕</button>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <button className="page-toggle-btn page-btn-create" onClick={() => {
                const matchday = localMatchdays.find(m => m.id === selectedMatchdayId);
                if (matchday) handleAddMatch(matchday);
              }}>
                <Plus size={14} /> Neues Spiel hinzufügen
              </button>
            </div>

            {matchdayMatches.length === 0 ? (
              <div className="page-empty-state"><p>Noch keine Spiele für diesen Spieltag erstellt.</p></div>
            ) : (
              <div className="cards-grid">
                {matchdayMatches.map((match) => {
                  const statusInfo = getLocalMatchStatusInfo(match.status);
                  return (
                    <div key={match.id} className="page-card" style={{ opacity: match.isActive ? 1 : 0.7, border: match.isActive ? '1px solid #e5e7eb' : '1px solid #ef4444' }}>
                      <div className="page-card-header">
                        <h4>{formatLocalMatchTeams(match)}</h4>
                        <div className="page-card-badges">
                          <span className="page-badge" style={{ backgroundColor: statusInfo.color }}>{statusInfo.icon} {statusInfo.label}</span>
                          {!match.isActive && <span className="page-badge" style={{ backgroundColor: '#dc3545' }}>Inaktiv</span>}
                        </div>
                      </div>

                      <div className="page-card-body">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem', background: '#f8f9fa', borderRadius: '6px', marginBottom: '0.5rem' }}>
                          <span>{match.homeTeam}</span>
                          <strong style={{ color: '#3b82f6' }}>{formatLocalMatchResult(match)}</strong>
                          <span>{match.awayTeam}</span>
                        </div>
                        <div className="page-card-info">
                          <div><CalendarDays size={12} /> <strong>Geplant:</strong> {formatDateTime(match.scheduledDate)}</div>
                          {match.actualDate && <div><CheckCircle size={12} /> <strong>Tatsächlich:</strong> {formatDateTime(match.actualDate)}</div>}
                          {match.location && <div><MapPin size={12} /> <strong>Ort:</strong> {match.location}</div>}
                          {match.referee && <div><User size={12} /> <strong>Schiedsrichter:</strong> {match.referee}</div>}
                        </div>
                      </div>

                      <div className="page-card-actions">
                        <button className="page-btn-icon edit" onClick={() => handleEditMatch(match)} title="Bearbeiten"><Pencil size={14} /></button>
                        <button className="page-btn-icon" onClick={() => handleToggleMatchActive(match)} title={match.isActive ? 'Deaktivieren' : 'Aktivieren'}>
                          {match.isActive ? <Pause size={14} /> : <Play size={14} />}
                        </button>
                        <button className="page-btn-icon delete" onClick={() => handleDeleteMatch(match)} title="Löschen"><Trash2 size={14} /></button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Match Form Modal */}
      {showMatchForm && (
        <LocalMatchForm
          match={editingMatch || undefined}
          onSave={editingMatch ?
            (data) => handleUpdateMatch(editingMatch.id, data as UpdateLocalMatchRequest) :
            (data) => handleCreateMatch(data as CreateLocalMatchRequest)
          }
          onCancel={() => {
            setShowMatchForm(false);
            setEditingMatch(null);
          }}
          loading={matchFormLoading}
        />
      )}

      {/* Match Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showMatchDeleteDialog}
        title="Spiel löschen"
        message={`Möchten Sie das Spiel "${matchToDelete ? formatLocalMatchTeams(matchToDelete) : ''}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`}
        confirmText="Löschen"
        cancelText="Abbrechen"
        onConfirm={confirmDeleteMatch}
        onCancel={() => {
          setShowMatchDeleteDialog(false);
          setMatchToDelete(null);
        }}
        loading={matchDeleteLoading}
        type="danger"
      />

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
    </div>
  );
};

export default LocalMatchdays;
